import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {Button} from 'primereact/button';
import {Checkbox} from 'primereact/checkbox';
import {Calendar} from 'primereact/calendar';
import {InputText} from 'primereact/inputtext';
import {InputMask} from 'primereact/inputmask';
import {InputNumber} from 'primereact/inputnumber';
import {suggestedDelivery, rateReference, rateLimits} from './receiptBillingPolicy';

export default function ReceiptBillingFields({data, onChange, external, receiptType, section}) {
  const issue = external ? data.issue_date : moment().format('YYYY-MM-DD');
  const reference = rateReference(data, receiptType, issue);
  const futureReference = reference && moment(reference, 'YYYY-MM-DD', true).isAfter(moment(), 'day');
  const manual = data.exchange_rate_mode === 'MANUAL';
  const advance = (receiptType || '').includes('ADVANCE');
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [changed, setChanged] = useState(false);
  const companyId = data.company?.id ?? data.company;
  const referenceKey = `${companyId}:${data.currency}:${reference}`;
  const previousReference = useRef(referenceKey);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  useEffect(() => {
    if (previousReference.current !== referenceKey && manual) setChanged(true);
    previousReference.current = referenceKey;
  }, [referenceKey, manual]);
  useEffect(() => {
    if (section !== 'rate' || !data.currency || data.currency === 'HUF' || manual) { setLoading(false); return; }
    let active = true;
    setError(null);
    if (!reference || !companyId) {
      setLoading(false);
      setError(!companyId ? 'Válaszd ki a vállalkozást.' : advance ? 'Add meg az előleg jóváírásának dátumát.' : 'Add meg a teljesítés dátumát.');
      changeRef.current({exchange_rate: null, exchangeRate: null, exchange_rate_date: null, exchange_rate_source: null});
      return;
    }
    setLoading(true);
    axios.get(process.env.REACT_APP_API_HOST + '/api/v2/receipt-exchange-rate', {
      headers: {Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')},
      params: {currency: data.currency, reference_date: reference, company: companyId},
    }).then(response => {
      if (!active) return;
      const quote = response.data.data;
      changeRef.current({exchange_rate: quote.rate, exchangeRate: quote.rate, exchange_rate_date: quote.date, exchange_rate_source: quote.source});
    }).catch(e => {
      if (!active) return;
      setError(e.response?.data?.message || 'Ehhez a dátumhoz nem érhető el árfolyam.');
      changeRef.current({exchange_rate: null, exchangeRate: null, exchange_rate_date: null, exchange_rate_source: null});
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [section, data.currency, companyId, reference, manual, advance, retry]);
  const dateInput = (name, label) => <div className="p-field p-fluid">
    <label htmlFor={name}>{label}</label>
    <div className="p-inputgroup calendar-icon-only">
      <InputMask id={name} mask="9999-99-99" placeholder="ÉÉÉÉ-HH-NN" value={data[name] || ''}
        onChange={e => { if (!e.value.includes('_')) onChange({[name]: e.value || null}); }}/>
      <Calendar value={data[name] ? moment(data[name], 'YYYY-MM-DD').toDate() : null}
        locale="hu" dateFormat="yy-mm-dd" showIcon
        onChange={e => onChange({[name]: e.value ? moment(e.value).format('YYYY-MM-DD') : null})}/>
    </div>
  </div>;
  if (section === 'period') {
    const suggestion = suggestedDelivery(data, issue);
    return <div className="p-col-12">
      <div className="p-field-checkbox">
        <Checkbox inputId="continuous_fulfillment" checked={!!data.continuous_fulfillment}
          onChange={e => onChange({continuous_fulfillment: e.checked})}/>
        <label htmlFor="continuous_fulfillment">Időszakos elszámolás</label>
      </div>
      {data.continuous_fulfillment && <div className="p-grid">
        <div className="p-col-12 p-md-6 p-lg-4">{dateInput('settlement_period_start', 'Időszak kezdete')}</div>
        <div className="p-col-12 p-md-6 p-lg-4">{dateInput('settlement_period_end', 'Időszak vége')}</div>
        {data.settlement_period_start && data.settlement_period_end && data.settlement_period_start > data.settlement_period_end && <div className="p-col-12"><small role="alert" className="p-error">Az időszak vége nem előzheti meg a kezdetét.</small></div>}
        {suggestion && suggestion !== data.delivery_date && <div className="p-col-12"><small role="status">A megadott dátumok alapján javasolt teljesítés: {suggestion}.</small>
          <Button type="button" label="Alkalmaz" icon="pi pi-check" className="p-button-success p-ml-2" onClick={() => onChange({delivery_date: suggestion})}/>
        </div>}
      </div>}
      {advance && <div className="p-grid"><div className="p-col-12 p-md-6 p-lg-4">{dateInput('advance_received_date', 'Előleg jóváírásának napja')}</div></div>}
    </div>;
  }
  if (!data.currency || data.currency === 'HUF') return null;
  const rate = data.exchange_rate ?? data.exchangeRate;
  const limits = rateLimits[data.currency];
  const invalid = rate != null && (!Number.isFinite(Number(rate)) || Number(rate) <= 0 || (limits && (Number(rate) < limits[0] || Number(rate) > limits[1])));
  const basisLabel = advance ? 'Előleg jóváírásának napja szerinti árfolyam'
    : data.continuous_fulfillment || (receiptType || '').includes('PROFORMA')
      ? 'Kiállítás napja szerinti árfolyam' : 'Teljesítés napja szerinti árfolyam';
  return <div className="p-col-12">
    <div className="p-d-flex p-ai-center p-flex-wrap" aria-live="polite">
      <span>{loading ? 'Árfolyam lekérése…' : `1 ${data.currency} = ${rate == null ? '—' : Number(rate).toLocaleString('hu-HU', {maximumFractionDigits: 6})} Ft`}
        {data.exchange_rate_date && ` · ${data.exchange_rate_date}`}
        {data.exchange_rate_source && ` · ${data.exchange_rate_source}`}
      </span>
      <Button type="button" icon="pi pi-pencil" className="p-button-warning p-button-sm p-ml-2" aria-label="Árfolyam módosítása"
        onClick={() => {setEditing(!editing); onChange({exchange_rate_mode: 'MANUAL', exchange_rate_source: data.exchange_rate_source || ''});}}/>
      {manual && <Button type="button" label="Visszaállítás" icon="pi pi-refresh" className="p-button-secondary p-ml-2"
        onClick={() => {setChanged(false); setEditing(false); onChange({exchange_rate_mode: 'AUTO'});}}/>}
    </div>
    <small className="p-d-block p-mt-1">{manual ? 'Kézzel megadott árfolyam' : futureReference && !error && !loading && rate != null ? 'A jövőbeli dátumhoz a legfrissebb árfolyamot alkalmazzuk' : basisLabel}</small>
    {editing && <div className="p-mt-3">
      <div className="p-grid">
        <div className="p-col-12 p-md-6 p-lg-4"><div className="p-field p-fluid">
          <label htmlFor="receipt_exchange_rate">Árfolyam (HUF)</label>
          <InputNumber inputId="receipt_exchange_rate" locale="hu-HU" minFractionDigits={2} maxFractionDigits={6}
            value={rate == null ? null : Number(rate)} className={invalid ? 'p-invalid' : ''}
            onValueChange={e => onChange({exchange_rate: e.value, exchangeRate: e.value, exchange_rate_mode: 'MANUAL'})}/>
        </div></div>
        <div className="p-col-12 p-md-6 p-lg-4">{dateInput('exchange_rate_date', 'Árfolyam dátuma')}</div>
        <div className="p-col-12 p-md-6 p-lg-4"><div className="p-field p-fluid">
          <label htmlFor="exchange_rate_source">Árfolyam forrása</label>
          <InputText id="exchange_rate_source" value={data.exchange_rate_source || ''}
            onChange={e => onChange({exchange_rate_source: e.target.value})}/>
        </div></div>
      </div>
      <Button type="button" label="Kész" icon="pi pi-check" className="p-button-success p-button-sm"
        disabled={invalid || rate == null || !data.exchange_rate_date || !data.exchange_rate_source} onClick={() => setEditing(false)}/>
    </div>}
    {manual && changed && <small role="status" className="p-d-block">A dátum megváltozott. Ellenőrizd a kézi árfolyamot.</small>}
    {invalid && <small role="alert" className="p-error p-d-block">{limits ? `1 ${data.currency} árfolyama ${limits[0]}–${limits[1]} HUF között adható meg.` : 'Érvénytelen árfolyam.'}</small>}
    {!manual && error && <div role="alert" className="p-error">{error}
      {!futureReference && <Button type="button" label="Újrapróbálás" icon="pi pi-refresh" className="p-button-text p-button-sm p-ml-2" onClick={() => setRetry(n => n + 1)}/>}
    </div>}
  </div>;
}
